/**
 * useCanvasPanning Hook
 * Handles canvas panning with mouse drag
 */

import { useEffect, useRef, RefObject, useCallback } from 'react'
import { dia } from '@joint/core'
import { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { EditorTool } from '../../../map-editor-shared/store/uiStore'
import { MAX_MAP_WIDTH, MAX_MAP_HEIGHT } from '../constants/mapBounds'

export function useCanvasPanning(
  paper: dia.Paper | null,
  graph: dia.Graph | null,
  canvasRef: RefObject<HTMLDivElement>,
  onBlankClick?: () => void,
  rotation: number = 0,
  selectedObjectType: ObjectType | null = null,
  currentTool: EditorTool = null
) {
  const isPanning = useRef(false)
  const lastMousePosition = useRef({ x: 0, y: 0 })
  const hasMoved = useRef(false)
  const cachedMapBounds = useRef<{ x: number; y: number; width: number; height: number } | null>(null)

  // Rebuild bounds cache when graph elements change
  useEffect(() => {
    if (!graph || !paper) return

    const invalidateCache = () => { cachedMapBounds.current = null }
    graph.on('add', invalidateCache)
    graph.on('remove', invalidateCache)

    return () => {
      graph.off('add', invalidateCache)
      graph.off('remove', invalidateCache)
    }
  }, [graph, paper])

  // Calculate map bounds (cached — O(n) only on cache miss)
  //
  // 기존: 항상 MAX_MAP_WIDTH × MAX_MAP_HEIGHT (2000×2000) 고정.
  //       normalize 보정으로 graph 가 17000px 같이 커지면 사용자가 hand tool 로
  //       graph 끝까지 panning 불가능.
  // 수정: graph 실측 bbox 우선 사용. 작은 도면용 fallback 으로만 MAX_MAP 사용.
  //       panning 범위에 약간의 여유(graph 크기의 50% padding)를 더해 어느 쪽으로든
  //       객체를 화면 가운데까지 끌어올 수 있게 한다.
  const calculateMapBounds = useCallback(() => {
    if (cachedMapBounds.current) return cachedMapBounds.current
    if (!graph || !paper) return null

    const cells = graph.getCells()
    let minX = Infinity, minY = Infinity
    let maxX = -Infinity, maxY = -Infinity

    cells.forEach(cell => {
      if (cell.isElement()) {
        const bbox = cell.getBBox()
        minX = Math.min(minX, bbox.x)
        minY = Math.min(minY, bbox.y)
        maxX = Math.max(maxX, bbox.x + bbox.width)
        maxY = Math.max(maxY, bbox.y + bbox.height)
      }
    })

    if (cells.length === 0 || minX === Infinity) {
      // No elements: fall back to fixed map size at viewport center
      const currentTranslate = paper.translate()
      const currentScale = paper.scale().sx
      const viewportEl = paper.el.parentElement
      const viewportWidth = viewportEl?.clientWidth || 800
      const viewportHeight = viewportEl?.clientHeight || 600
      const viewportW = viewportWidth / currentScale
      const viewportH = viewportHeight / currentScale
      const viewportX = -currentTranslate.tx / currentScale
      const viewportY = -currentTranslate.ty / currentScale
      const centerX = viewportX + viewportW / 2
      const centerY = viewportY + viewportH / 2
      const bounds = {
        x: centerX - MAX_MAP_WIDTH / 2,
        y: centerY - MAX_MAP_HEIGHT / 2,
        width: MAX_MAP_WIDTH,
        height: MAX_MAP_HEIGHT,
      }
      cachedMapBounds.current = bounds
      return bounds
    }

    // Graph 실측 bbox + 50% padding (panning 시 여유 확보)
    const rawWidth = maxX - minX
    const rawHeight = maxY - minY
    const padW = rawWidth * 0.5
    const padH = rawHeight * 0.5
    const width = Math.max(rawWidth + padW * 2, MAX_MAP_WIDTH)
    const height = Math.max(rawHeight + padH * 2, MAX_MAP_HEIGHT)
    const centerX = (minX + maxX) / 2
    const centerY = (minY + maxY) / 2

    const bounds = {
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    }
    cachedMapBounds.current = bounds
    return bounds
  }, [graph, paper])

  useEffect(() => {
    if (!paper || !graph) return

    const startPanning = (evt: dia.Event) => {
      // Don't start panning if user is in object creation mode or move mode
      if (selectedObjectType || currentTool === 'move') {
        return
      }

      isPanning.current = true
      hasMoved.current = false
      lastMousePosition.current = { x: evt.clientX || 0, y: evt.clientY || 0 }
      if (canvasRef.current) {
        canvasRef.current.style.cursor = 'grabbing'
      }
    }

    const handleBlankPointerDown = (evt: dia.Event) => {
      startPanning(evt)
    }

    // NOTE: 요소 위 pointerdown 에서 panning 을 시작하지 않는다.
    // 그래야 hand/drag 어느 모드든 click selection (cell:pointerclick) 이 정상 발화한다.
    // pan 은 빈 영역 드래그로만 동작.

    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanning.current || !paper) return

      const rawDx = e.clientX - lastMousePosition.current.x
      const rawDy = e.clientY - lastMousePosition.current.y

      // Track if user has moved (not just clicked)
      if (Math.abs(rawDx) > 2 || Math.abs(rawDy) > 2) {
        hasMoved.current = true
      }

      // No rotation transformation needed - mouse delta applies directly to paper
      // The visual rotation is applied via CSS on the container, not the paper itself
      const dx = rawDx
      const dy = rawDy

      const currentTranslate = paper.translate()
      let newTx = currentTranslate.tx + dx
      let newTy = currentTranslate.ty + dy

      // Apply map bounds constraints (relaxed - allow viewport center to move within map)
      const mapBounds = calculateMapBounds()
      if (mapBounds) {
        const currentScale = paper.scale().sx
        const viewportEl = paper.el.parentElement
        const viewportWidth = viewportEl?.clientWidth || 800
        const viewportHeight = viewportEl?.clientHeight || 600

        // Calculate viewport center in world coordinates
        const viewportW = viewportWidth / currentScale
        const viewportH = viewportHeight / currentScale
        const viewportCenterX = -newTx / currentScale + viewportW / 2
        const viewportCenterY = -newTy / currentScale + viewportH / 2

        // Constrain viewport center to stay within map bounds (allows edges to be visible)
        const constrainedCenterX = Math.max(
          mapBounds.x,
          Math.min(viewportCenterX, mapBounds.x + mapBounds.width)
        )
        const constrainedCenterY = Math.max(
          mapBounds.y,
          Math.min(viewportCenterY, mapBounds.y + mapBounds.height)
        )

        // Convert back to translate coordinates
        newTx = -(constrainedCenterX - viewportW / 2) * currentScale
        newTy = -(constrainedCenterY - viewportH / 2) * currentScale
      }

      paper.translate(newTx, newTy)

      // Debug: Log translate values occasionally (every 10 moves)
      if (Math.random() < 0.1) {
        const currentScale = paper.scale()
      }

      lastMousePosition.current = { x: e.clientX, y: e.clientY }
    }

    const handleMouseUp = () => {
      // NOTE: Blank click selection/deselection is handled by useCanvasClickSelection.
      // This hook only handles panning (drag). No onBlankClick here to avoid
      // race conditions with the click selection hook.

      isPanning.current = false
      hasMoved.current = false
      if (canvasRef.current) {
        canvasRef.current.style.cursor = currentTool === 'move' ? 'crosshair' : 'grab'
      }
    }

    // Set initial cursor based on current tool
    if (canvasRef.current) {
      canvasRef.current.style.cursor = currentTool === 'move' ? 'crosshair' : 'grab'
    }

    paper.on('blank:pointerdown', handleBlankPointerDown)
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      paper.off('blank:pointerdown', handleBlankPointerDown)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [paper, graph, canvasRef, onBlankClick, rotation, selectedObjectType, currentTool, calculateMapBounds])
}
